import React from 'react'
import { Box, ListItemButton, ListItemIcon, ListItemText } from '@mui/material'
import { useDraggable } from '@dnd-kit/core'
import { WidgetTypeOption, dragGhostOption, dragGhostTitle } from '../widgetTaxonomy'
import { DashboardCell } from '../../../types/gotypes'

interface WidgetPaletteCardProps {
  widget: WidgetTypeOption
}

/**
 * One draggable tile in the widget palette. Dragging it onto the canvas
 * creates a widget of this type — see each surface's `onDragEnd`, which reads
 * `active.data.current` to tell a palette drag (`source: 'palette'`) apart
 * from a move of an existing widget (`source: 'canvas'`). The two shapes are
 * declared once, in `layoutDnd.ts`.
 */
const WidgetPaletteCard: React.FC<WidgetPaletteCardProps> = ({ widget }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette-${widget.type}`,
    data: { source: 'palette', widgetType: widget.type },
  })

  return (
    <ListItemButton
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      disableRipple
      sx={{
        borderRadius: 1,
        mb: 0.5,
        cursor: 'grab',
        opacity: isDragging ? 0.4 : 1,
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <ListItemIcon sx={{ minWidth: 36 }}>{widget.icon}</ListItemIcon>
      <ListItemText
        primary={widget.label}
        secondary={widget.subtitle}
        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
        secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary', sx: { display: 'block', lineHeight: 1.3 } }}
      />
    </ListItemButton>
  )
}

/** A lightweight ghost rendered inside the DragOverlay while a palette tile is being dragged. */
export const WidgetPaletteDragGhost: React.FC<{ widget: WidgetTypeOption }> = ({ widget }) => (
  <Box
    sx={{
      display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
      bgcolor: 'background.paper', borderRadius: 1, boxShadow: 4,
      border: '1px solid', borderColor: 'info.main',
    }}
  >
    {widget.icon}
    <Box component="span" sx={{ fontSize: 13, fontWeight: 500 }}>{widget.label}</Box>
  </Box>
)

/**
 * The ghost for dragging an EXISTING widget around the canvas (#1129): a
 * compact card naming the widget being moved — its own title, with the
 * widget-type icon beside it — so two charts with different titles produce
 * visibly different ghosts. Untitled widgets fall back to the type label
 * (via `dragGhostOption`, which never returns undefined — #1130).
 *
 * Deliberately a static card, never a live copy of the widget: a second
 * chart tree re-rendering on every pointer move would judder. And no blue —
 * during a drag, blue belongs to the drop bars alone.
 */
export const WidgetDragGhost: React.FC<{ cell: DashboardCell }> = ({ cell }) => {
  const option = dragGhostOption(cell)
  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
        bgcolor: 'background.paper', borderRadius: 1, boxShadow: 4,
        border: '1px solid', borderColor: 'divider',
        maxWidth: 320, cursor: 'grabbing',
      }}
    >
      {option.icon}
      <Box
        component="span"
        sx={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        {dragGhostTitle(cell) ?? option.label}
      </Box>
    </Box>
  )
}

export default WidgetPaletteCard
